/**
 * Kostenpositionen der Genehmigung, über alle Anträge des Projekts hinweg.
 *
 * Zwei Zahlen interessieren den Projektierer: was die Genehmigung kosten wird (Plankosten) und
 * was bisher tatsächlich angefallen ist (Istkosten, beim Nachweis mit der Freigabe erfasst).
 * Beide Summen decken sich mit dem abgeleiteten CAPEX-Abschnitt "Genehmigung" in der Kalkulation.
 *
 * Die Summe der Plankosten heißt bewusst "Erfasste Plankosten" und nicht "Gesamtkosten": sie
 * ist die Summe der angelegten Positionen, nicht die des Vorhabens. Solange nicht alle
 * Gutachten erfasst sind, ist sie systematisch zu niedrig – eine Top-down-Schätzung gibt es
 * hier bewusst nicht, die lebt als Kalkulations-Platzhalter im CAPEX-Abschnitt "Genehmigung".
 *
 * Die Tabelle zeigt beide Arten von Positionen in einer Liste:
 * - **nachweisgebunden** – Bezeichnung kommt vom Nachweis und ist hier nicht editierbar;
 *   angelegt werden diese Positionen im Nachweis-Detail, nicht hier.
 * - **ohne Nachweisbezug** – Behördengebühren, Verfahrenskosten. Nur diese tragen eine eigene
 *   Bezeichnung, und nur für sie ist sie Pflicht.
 */
import React, { useCallback, useMemo, useState } from "react";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Button from "../../../common/inputs/Button";
import StatChip from "../../../common/display/StatChip";
import Section from "../../../layout/Section";
import BaseTable, {
  TableColumn,
  TableData,
} from "../../../tables/common/BaseTable";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import EditableLabel from "../../../common/inputs/EditableLabel";
import { formatCurrency } from "../../../../utils/formatters";
import { colors, fonts } from "../../../../styles/theme";
import {
  PermitCostItemCreate,
  PermitCostItemUpdate,
  PermitCostSummary,
  PermitProcedure,
  ProjectPermitCostItem,
} from "../../../../types";

const COLUMNS: TableColumn[] = [
  // Explizit linksbündig: die Inhalte sind es auch, ein rechtsbündiger Titel darüber
  // stünde nicht über seiner Spalte.
  {
    key: "anzeige_bezeichnung",
    label: "Bezeichnung",
    align: "left",
    sortable: true,
  },
  {
    key: "betrag_geplant",
    label: "Geplante Kosten",
    align: "right",
    sortable: true,
    width: 280,
  },
  {
    key: "betrag_ist",
    label: "Bisher angefallene Kosten",
    align: "right",
    sortable: true,
    width: 280,
  },
];

interface CostItemsSectionProps {
  costItems: ProjectPermitCostItem[];
  costSummary: PermitCostSummary;
  /** Anträge des Projekts – Auswahl beim Anlegen einer Position */
  procedures: PermitProcedure[];
  canEdit: boolean;
  onCreate: (
    permitProcedureId: string,
    data: PermitCostItemCreate,
  ) => Promise<void>;
  onUpdate: (costItemId: string, data: PermitCostItemUpdate) => Promise<void>;
  onDelete: (costItemId: string) => Promise<void>;
}

const CostItemsSection: React.FC<CostItemsSectionProps> = ({
  costItems,
  costSummary,
  procedures,
  canEdit,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  /** Offenes Auswahlmenü der Anlegen-Aktion; nur nötig, wenn es mehrere Anträge gibt */
  const [antragsAuswahl, setAntragsAuswahl] = useState<HTMLElement | null>(
    null,
  );
  const tableData: TableData[] = useMemo(
    () => costItems.map((item) => ({ ...item })),
    [costItems],
  );

  const renderCell = useCallback(
    (column: TableColumn, row: TableData) => {
      const item = row as ProjectPermitCostItem;

      if (column.key === "anzeige_bezeichnung") {
        // Nachweisgebundene Zeilen tragen den Namen des Nachweises – umbenannt wird dort,
        // nicht hier. Ein editierbares Feld, das den Nachweisnamen zeigt, aber etwas anderes
        // speichert, wäre eine zweite Wahrheit.
        if (item.evidence_id) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DescriptionOutlinedIcon
                fontSize="small"
                sx={{ color: colors.icon.subtle }}
              />
              <Typography variant="body2">
                {item.anzeige_bezeichnung}
              </Typography>
            </Box>
          );
        }

        return (
          <EditableLabel
            value={item.anzeige_bezeichnung}
            editable={canEdit}
            variant="body2"
            stopPropagation
            onSave={(value) => onUpdate(item.id, { bezeichnung: value })}
          />
        );
      }

      const betrag =
        column.key === "betrag_geplant" ? item.betrag_geplant : item.betrag_ist;

      if (!canEdit) {
        return (
          <Typography
            variant="body2"
            sx={{ fontFamily: fonts.monospace, textAlign: "right" }}
          >
            {betrag == null ? "—" : formatCurrency(betrag)}
          </Typography>
        );
      }

      return (
        <InputFieldNumber
          label=""
          value={betrag == null ? "" : String(betrag)}
          // Leeres Feld → null, nicht 0: "nicht gepflegt" bleibt von "kostet nichts"
          // unterscheidbar.
          onBlur={(value) => {
            const parsed = value.trim() === "" ? null : parseFloat(value);
            onUpdate(item.id, {
              [column.key]: parsed !== null && isNaN(parsed) ? null : parsed,
            } as PermitCostItemUpdate);
          }}
          suffix="€"
          allowDecimals={false}
          textAlign="right"
        />
      );
    },
    [canEdit, onUpdate],
  );

  const handleDelete = useCallback(
    (row: TableData) => {
      onDelete(row.id);
    },
    [onDelete],
  );

  const anlegen = useCallback(
    (permitProcedureId: string) => {
      setAntragsAuswahl(null);
      onCreate(permitProcedureId, { bezeichnung: "Neue Kostenposition" });
    },
    [onCreate],
  );

  /**
   * Eine Kostenposition gehört immer zu genau einem Antrag. Bei mehreren Anträgen muss der
   * Nutzer wählen; bei genau einem ist die Wahl eindeutig und der Zwischenschritt entfällt.
   */
  const handleAdd = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (procedures.length === 1) {
        anlegen(procedures[0].id);
        return;
      }
      setAntragsAuswahl(event.currentTarget);
    },
    [procedures, anlegen],
  );

  return (
    <Section
      /* headerContent statt title: die Summen sollen direkt neben der Überschrift stehen,
         `actions` rendert dagegen immer rechtsbündig. */
      headerContent={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: fonts.weights.light, color: "text.primary" }}
          >
            Kostenpositionen
          </Typography>
          <StatChip
            value={formatCurrency(costSummary.summe_geplant)}
            label="Erfasste Plankosten"
            backgroundColor={colors.brand.teal50}
            valueColor={colors.brand.teal600}
          />
          <StatChip
            value={formatCurrency(costSummary.summe_ist)}
            label="Bereits beauftragt"
            backgroundColor={colors.brand.teal50}
            valueColor={colors.brand.teal600}
          />
        </Box>
      }
      actions={
        canEdit && procedures.length > 0 ? (
          <Button variant="primary" onClick={handleAdd} startIcon={<AddIcon />}>
            Position anlegen
          </Button>
        ) : undefined
      }
      noPadding
    >
      <BaseTable
        columns={COLUMNS}
        data={tableData}
        renderCell={renderCell}
        onDelete={canEdit ? handleDelete : undefined}
        confirmDelete
        confirmDeleteMessage={(row) =>
          `Kostenposition "${row.anzeige_bezeichnung}" wirklich löschen?`
        }
        disablePagination
        emptyMessage="Noch keine Kostenpositionen erfasst"
      />

      <Menu
        open={!!antragsAuswahl}
        anchorEl={antragsAuswahl}
        onClose={() => setAntragsAuswahl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {procedures.map((procedure) => (
          <MenuItem key={procedure.id} onClick={() => anlegen(procedure.id)}>
            <Typography variant="body2" noWrap>
              {procedure.bezeichnung || procedure.procedure_type.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Section>
  );
};

export default CostItemsSection;
